import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, Mic } from "lucide-react";
import { toast } from "sonner";
import { PersonaSelector, loadPersonaKey, savePersonaKey, type PersonaKey } from "@/components/jarvis/PersonaSelector";

type Mode = "passive" | "invocation" | "proactive";

interface Persona {
  id: string;
  name: string;
}

export default function JarvisCreateSession() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [eventName, setEventName] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [mode, setMode] = useState<Mode>("invocation");
  const [personaId, setPersonaId] = useState<string>("");
  const [language, setLanguage] = useState("pt-BR");
  const [maxGuests, setMaxGuests] = useState<string>("");
  const [allowGuestQuestions, setAllowGuestQuestions] = useState(false);
  const [ephemeral, setEphemeral] = useState(false);
  const [personaKey, setPersonaKey] = useState<PersonaKey>(() => loadPersonaKey());

  useEffect(() => {
    if (!user?.id) return;
    void (async () => {
      const { data: mem } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id)
        .limit(1)
        .single();
      if (!mem?.org_id) return;
      setOrgId(mem.org_id);

      const { data: org } = await supabase
        .from("organizations")
        .select("jarvis_default_persona_id, jarvis_default_mode")
        .eq("id", mem.org_id)
        .single();
      if (org?.jarvis_default_persona_id) setPersonaId(org.jarvis_default_persona_id);
      if (org?.jarvis_default_mode) setMode(org.jarvis_default_mode as Mode);

      const { data: ps } = await supabase
        .from("syra_ai_agents_safe")
        .select("id, name")
        .eq("org_id", mem.org_id)
        .order("name");
      setPersonas(ps ?? []);
    })();
  }, [user?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) {
      toast.error("Organização não encontrada");
      return;
    }
    if (!eventName.trim()) {
      toast.error("Informe o nome do evento");
      return;
    }
    setSubmitting(true);
    try {
      const createRes = await supabase.functions.invoke("jarvis-session", {
        body: {
          action: "create",
          org_id: orgId,
          mode,
          language,
          persona_key: personaKey,
          ...(personaId ? { persona_id: personaId } : {}),
          event_name: eventName.trim(),
          event_description: eventDescription.trim() || null,
          max_guests: maxGuests ? parseInt(maxGuests, 10) : null,
          allow_guest_questions: allowGuestQuestions,
          ephemeral,
        },
      });
      if (createRes.error || !createRes.data?.session?.id) {
        throw new Error(createRes.error?.message || "Falha ao criar sessão");
      }
      const sid = createRes.data.session.id as string;

      // Warmup
      await supabase.functions.invoke("jarvis-session", {
        body: { action: "warmup", session_id: sid },
      });

      toast.success("Sessão criada");
      navigate(`/jarvis/session/${sid}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Erro: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="mb-6">
        <Button asChild variant="ghost" size="sm" className="mb-3">
          <Link to="/jarvis"><ArrowLeft className="w-4 h-4 mr-2" /> Voltar</Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight font-headline">Nova Sessão Jarvis</h1>
        <p className="text-muted-foreground mt-1">
          Configure os parâmetros do evento. Após criada, a sessão entra em <code>warmup</code> e fica pronta para iniciar.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Detalhes do evento</CardTitle>
            <CardDescription>Identificação e contexto da sessão.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="event_name">Nome do evento *</Label>
              <Input
                id="event_name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="Ex: Reunião semanal de produto"
                maxLength={120}
                required
              />
            </div>
            <div>
              <Label htmlFor="event_description">Descrição</Label>
              <Textarea
                id="event_description"
                value={eventDescription}
                onChange={(e) => setEventDescription(e.target.value)}
                placeholder="Pauta, objetivos, contexto…"
                rows={3}
                maxLength={1000}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Comportamento da IA</CardTitle>
            <CardDescription>Persona, idioma e modo de interação.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-2 block">Persona built-in (voz)</Label>
              <PersonaSelector
                value={personaKey}
                onChange={(v) => { setPersonaKey(v); savePersonaKey(v); }}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Define a voz e o tom de resposta. Sobrescreve a persona da org se selecionada abaixo.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mode">Modo</Label>
                <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
                  <SelectTrigger id="mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passive">Passivo (só ouve)</SelectItem>
                    <SelectItem value="invocation">Invocação (wake-word)</SelectItem>
                    <SelectItem value="proactive">Proativo (responde sempre)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="language">Idioma</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger id="language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                    <SelectItem value="en-US">English (US)</SelectItem>
                    <SelectItem value="es-ES">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="persona">Persona</Label>
              <Select value={personaId || "none"} onValueChange={(v) => setPersonaId(v === "none" ? "" : v)}>
                <SelectTrigger id="persona"><SelectValue placeholder="Padrão da organização" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem persona específica</SelectItem>
                  {personas.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Modo evento</CardTitle>
            <CardDescription>Acesso de convidados via QR Code (configurável depois).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="max_guests">Limite de convidados (opcional)</Label>
              <Input
                id="max_guests"
                type="number"
                min={1}
                max={10000}
                value={maxGuests}
                onChange={(e) => setMaxGuests(e.target.value)}
                placeholder="Sem limite"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label htmlFor="allow_qa" className="text-sm">Permitir perguntas do público</Label>
                <p className="text-xs text-muted-foreground">Convidados podem enviar perguntas pela página de audiência.</p>
              </div>
              <Switch id="allow_qa" checked={allowGuestQuestions} onCheckedChange={setAllowGuestQuestions} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label htmlFor="ephemeral" className="text-sm">Sessão efêmera</Label>
                <p className="text-xs text-muted-foreground">Apaga transcrições e artefatos automaticamente após o término.</p>
              </div>
              <Switch id="ephemeral" checked={ephemeral} onCheckedChange={setEphemeral} />
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex justify-end gap-2">
          <Button asChild variant="outline" type="button">
            <Link to="/jarvis">Cancelar</Link>
          </Button>
          <Button type="submit" disabled={submitting || !orgId}>
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Criando…</>
            ) : (
              <><Mic className="w-4 h-4 mr-2" /> Criar sessão</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
